import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import RichWinningResultEditor from './RichWinningResultEditor'
import useStore from '../hooks/useStore'
import { computeUnstoppableStreak } from '../utils/streakUtils'

const characterTraits = [
  {
    icon: '◉',
    title: 'Brutal Honesty',
    copy: 'Am I picking the real W, or the comfortable one?',
    failureMode: "W goes to zero when you set a decoy goal — a task that feels productive but isn't the thing that actually moves the needle. The root cause is self-deception, not laziness.",
    test: 'If I could only do ONE thing today and everything else disappeared, would I still pick this W?',
    leftLabel: 'Decoy',
    leftExample: 'Refine the pitch deck layout',
    rightLabel: 'Real',
    rightExample: 'Send 5 cold outreach messages to SaaS founders with personalized value props',
    bottomLine: 'This prevents W from being a decoy.'
  },
  {
    icon: '🛡',
    title: 'Decisive Protection',
    copy: 'Decided. Protected. Executing.',
    failureMode: "C (concentration) goes to zero when attention fragments. Because R = W × C, even a perfect W produces nothing if C collapses. The root cause isn't weak willpower — it's the absence of a pre-made decision.",
    test: 'Have I already decided what I will NOT do during my focus block?',
    leftLabel: 'Without This',
    leftExample: "I'll try to stay focused today",
    rightLabel: 'With This',
    rightExample: 'If Slack pings, I check at 12:30, not now — decision already made.',
    bottomLine: 'This prevents C from fragmenting.'
  },
  {
    icon: '⚡',
    title: 'Discomfort as Signal',
    copy: "If it feels uncomfortable, it's probably the one.",
    failureMode: "You can execute perfectly on something that doesn't matter. Discomfort is the body's signal that the task carries real stakes — which is exactly why it's high-leverage.",
    test: "Does this W make me slightly nervous? If not, I'm probably playing it safe.",
    leftLabel: 'Decoy',
    leftExample: 'Write documentation for the API',
    rightLabel: 'Real',
    rightExample: 'Call the prospect who went cold last week',
    bottomLine: 'This ensures W targets the highest-leverage action.'
  }
]

const characterChallenges = [
  {
    label: 'Honesty Check',
    question: "Is this the W you're avoiding — or the one you're hiding behind?",
    requiresInput: false
  },
  {
    label: 'Protection Check',
    question: "What will you refuse to do during today's focus block?",
    requiresInput: true,
    placeholder: 'e.g. No Slack until 12:30, no email during deep work'
  },
  {
    label: 'Signal Check',
    question: 'Does this W make you slightly nervous? If not, you might be playing safe.',
    requiresInput: false
  }
]

const todayISO = new Date().toISOString().split('T')[0]

function formatNavDate(iso) {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()
}

function MorningPanel() {
  const goalRef = useRef(null)
  const challengeInputRef = useRef(null)
  const challengeRef = useRef(null)
  const draftTimer = useRef(null)
  const navigate = useNavigate()
  const { goal, wScore, burningDesire, history, updateFormData, showToast, saveGoalDraft, saveDatePlan, loadDatePlan } = useStore()
  const { currentStreak } = computeUnstoppableStreak(history)

  // Date planning state
  const [plannedDate, setPlannedDate] = useState(todayISO)
  const [futureGoal, setFutureGoal] = useState('')
  const [futureWScore, setFutureWScore] = useState(7)
  const [futureSaving, setFutureSaving] = useState(false)
  const [futureLoading, setFutureLoading] = useState(false)
  const isToday = plannedDate === todayISO
  const isFuture = plannedDate > todayISO

  // Yesterday's W seed
  const [yesterdaySeed, setYesterdaySeed] = useState('')
  const [seedPopupOpen, setSeedPopupOpen] = useState(false)

  useEffect(() => {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const yISO = yesterday.toISOString().split('T')[0]
    loadDatePlan(yISO).then(data => {
      if (data?.tomorrowSeed) setYesterdaySeed(data.tomorrowSeed)
    })
  }, []) // eslint-disable-line

  const changeDate = (delta) => {
    const [y, m, d] = plannedDate.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    date.setDate(date.getDate() + delta)
    const next = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-')
    if (next < todayISO) return // no past dates
    setPlannedDate(next)
  }

  // Load saved plan when switching to a future date
  useEffect(() => {
    if (isToday) return
    setFutureLoading(true)
    setFutureGoal('')
    setFutureWScore(7)
    loadDatePlan(plannedDate).then(data => {
      if (data) {
        setFutureGoal(data.goal === 'No goal recorded' ? '' : data.goal)
        setFutureWScore(data.wScore)
      }
      setFutureLoading(false)
    })
  }, [plannedDate]) // eslint-disable-line

  const handleSavePlan = async () => {
    if (!futureGoal.trim()) { showToast('⚠', 'Write a W goal first!'); return }
    setFutureSaving(true)
    try {
      await saveDatePlan(plannedDate, { goal: futureGoal, wScore: futureWScore })
      showToast('✓', `Plan saved for ${formatNavDate(plannedDate)}`)
    } catch {
      showToast('✗', 'Save failed')
    } finally {
      setFutureSaving(false)
    }
  }
  const [activeTrait, setActiveTrait] = useState(null)
  const [isClosingTrait, setIsClosingTrait] = useState(false)
  const [challengeVisible, setChallengeVisible] = useState(false)
  const [challengeClosing, setChallengeClosing] = useState(false)
  const [protectionNotList, setProtectionNotList] = useState('')
  const [challengeConfirmed, setChallengeConfirmed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.sessionStorage.getItem('resultos-w-challenge-confirmed') === 'true'
  })
  const [checklist, setChecklist] = useState([
    false, false, false, false, false
  ])

  const checklistItems = [
    "Written my single W goal as a completed result, not a task",
    "Phone on DND or placed in another room",
    "All browser tabs closed except what I need for this session",
    "Notified team — deep work block, no interruptions for 90 min",
    "Committed to zero task-switching for this entire block"
  ]

  const handleGoalChange = (value) => {
    updateFormData({ goal: value })
    clearTimeout(draftTimer.current)
    draftTimer.current = setTimeout(() => saveGoalDraft(), 1500)
  }

  useEffect(() => {
    if (!activeTrait) return undefined

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeTraitModal()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [activeTrait])

  useEffect(() => {
    if (!challengeVisible || !activeChallenge?.requiresInput) return undefined

    const timer = setTimeout(() => {
      challengeInputRef.current?.focus()
    }, 120)

    return () => clearTimeout(timer)
  }, [challengeVisible])

  useEffect(() => {
    if (!challengeVisible) return undefined

    const timer = setTimeout(() => {
      challengeRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }, 80)

    return () => clearTimeout(timer)
  }, [challengeVisible])

  const handleWScoreChange = (value) => {
    updateFormData({ wScore: parseInt(value) })
    clearTimeout(draftTimer.current)
    draftTimer.current = setTimeout(() => saveGoalDraft(), 1500)
  }

  const toggleChecklistItem = (index) => {
    const newChecklist = [...checklist]
    newChecklist[index] = !newChecklist[index]
    setChecklist(newChecklist)
  }

  const openTraitModal = (trait) => {
    setIsClosingTrait(false)
    setActiveTrait(trait)
  }

  const closeTraitModal = () => {
    if (!activeTrait || isClosingTrait) return

    setIsClosingTrait(true)
    setTimeout(() => {
      setActiveTrait(null)
      setIsClosingTrait(false)
    }, 180)
  }

  const challengeIndex = new Date().getDate() % 3
  const activeChallenge = characterChallenges[challengeIndex]

  const finalizeGoalCommit = async () => {
    if (!goal.trim()) {
      showToast('⚠', 'Write your W goal first!')
      return
    }

    clearTimeout(draftTimer.current)
    try {
      await saveGoalDraft()
    } catch {
      // non-fatal — toast already shown by store on error
    }
    showToast('✓', 'Goal committed. Time to execute.')
    setTimeout(() => navigate('/focus'), 700)
  }

  const handleCommitGoal = () => {
    if (!goal.trim()) {
      showToast('⚠', 'Write your W goal first!')
      return
    }

    if (challengeConfirmed) {
      finalizeGoalCommit()
      return
    }

    setChallengeClosing(false)
    setChallengeVisible(true)
  }

  const handleRewriteW = () => {
    setChallengeClosing(true)
    setTimeout(() => {
      setChallengeVisible(false)
      setChallengeClosing(false)
      goalRef.current?.focus()
    }, 180)
  }

  const handleConfirmRealW = () => {
    setChallengeConfirmed(true)
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('resultos-w-challenge-confirmed', 'true')
    }

    setChallengeClosing(true)
    setTimeout(() => {
      setChallengeVisible(false)
      setChallengeClosing(false)
      finalizeGoalCommit()
    }, 180)
  }

  const completedCount = checklist.filter(Boolean).length
  const traitModal = activeTrait ? (
    <div
      className={`trait-modal-overlay ${isClosingTrait ? 'closing' : ''}`}
      onClick={closeTraitModal}
      role="presentation"
    >
      <div
        className={`trait-modal ${isClosingTrait ? 'closing' : ''}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="trait-modal-title"
      >
        <button type="button" className="trait-modal-close" onClick={closeTraitModal} aria-label="Close dialog">
          ×
        </button>
        <div className="trait-modal-header">
          <div className="morning-primer-icon trait-modal-icon" aria-hidden="true">{activeTrait.icon}</div>
          <div>
            <div className="c-lbl">Character Commitment</div>
            <div id="trait-modal-title" className="trait-modal-title">{activeTrait.title}</div>
          </div>
        </div>
        <div className="trait-modal-section trait-modal-failure">
          <div className="trait-modal-label">The Failure Mode</div>
          <div className="trait-modal-text">{activeTrait.failureMode}</div>
        </div>
        <div className="trait-modal-test">
          <div className="trait-modal-label">The Test</div>
          <div className="trait-modal-test-line">{activeTrait.test}</div>
        </div>
        <div className="trait-modal-section">
          <div className="trait-modal-label">Example</div>
          <div className="trait-modal-example-grid">
            <div className="trait-example trait-example-left">
              <div className="trait-example-label">{activeTrait.leftLabel}</div>
              <div className="trait-example-copy">{activeTrait.leftExample}</div>
            </div>
            <div className="trait-example trait-example-right">
              <div className="trait-example-label">{activeTrait.rightLabel}</div>
              <div className="trait-example-copy">{activeTrait.rightExample}</div>
            </div>
          </div>
        </div>
        <div className="trait-modal-bottom">{activeTrait.bottomLine}</div>
      </div>
    </div>
  ) : null

  return (
    <div className="panel active">
      <div className="pg-ey">Step 01 · Morning Ritual</div>

      {/* ── Burning Desire reminder ── */}
      {burningDesire ? (
        <div className="morning-why">
          <span className="morning-why-label">Your Why</span>
          <p className="morning-why-text">{burningDesire}</p>
        </div>
      ) : (
        <div className="morning-why morning-why--empty">
          <span className="morning-why-label">Your Why</span>
          <button className="morning-why-prompt" onClick={() => navigate('/monthly')}>
            Set your burning desire in Monthly Results Area →
          </button>
        </div>
      )}

      {/* ── Unstoppable streak nudge ── */}
      {currentStreak === 0 ? (
        <div className="morning-streak-nudge">
          Start your unstoppable streak today
        </div>
      ) : (
        <div className={`morning-streak-nudge${currentStreak >= 30 ? ' glow' : ''}${currentStreak >= 7 ? ' amber' : ''}`}>
          Day {currentStreak} of being unstoppable
        </div>
      )}

      <div className="morning-primer" aria-label="Character commitment">
        {characterTraits.map((trait) => (
          <button
            key={trait.title}
            type="button"
            className="morning-primer-item morning-primer-button"
            onClick={() => openTraitModal(trait)}
          >
            <div className="morning-primer-icon" aria-hidden="true">{trait.icon}</div>
            <div className="morning-primer-copy">
              <div className="morning-primer-title">{trait.title}</div>
              <div className="morning-primer-line">{trait.copy}</div>
            </div>
          </button>
        ))}
      </div>
      <div className="pg-ti">
        Set your <em>Winning Result</em> (W)
      </div>
      <div className="pg-su">
        The single most important act of the morning. Define the one result that will make the day a win.
        Write it so clearly that someone else would know exactly what done looks like.
      </div>

      {/* ── Date navigator ── */}
      <div className="morning-date-nav">
        <button className="canvas-month-btn" onClick={() => changeDate(-1)} disabled={isToday} style={{opacity: isToday ? 0.3 : 1}}>‹</button>
        <div className="morning-date-pill">
          {isToday
            ? <><span className="morning-date-today-dot"></span>TODAY</>
            : formatNavDate(plannedDate)
          }
        </div>
        <button className="canvas-month-btn" onClick={() => changeDate(1)}>›</button>
        {isFuture && (
          <span className="morning-date-plan-badge">Planning ahead</span>
        )}
      </div>

      {/* ── W Goal card ── */}
      <div className={`card mb24 morning-w-card${isFuture ? ' morning-w-future' : ''}`}>
        <div className="cg gold"></div>
        <div className="c-lbl" style={{display:'flex',alignItems:'center',gap:'8px'}}>
          W — Winning Result
          {isFuture && <span className="morning-future-lbl">Future plan · read-only on execution day</span>}
        </div>

        {isFuture ? (
          /* ── FUTURE DATE: plan-ahead view ── */
          futureLoading ? (
            <div style={{padding:'20px 0',color:'var(--text3)',fontSize:'12px',fontStyle:'italic'}}>Loading…</div>
          ) : (
            <>
              <label htmlFor="future-w-goal" style={{display:'block',fontSize:'11px',color:'var(--text2)',fontWeight:'700',marginBottom:'10px',marginTop:'16px'}}>
                Write your W for {formatNavDate(plannedDate)}
              </label>
              <RichWinningResultEditor
                storageKey={`plan-${plannedDate}`}
                placeholder="e.g. Ship the onboarding flow with all 3 steps working end-to-end."
                value={futureGoal}
                onChange={setFutureGoal}
              />
              <div style={{fontSize:'11px',color:'var(--text3)',marginTop:'10px',fontStyle:'italic'}}>
                This goal will be pre-loaded when you open Morning Setup on {formatNavDate(plannedDate)}.
              </div>
              <div className="sw" style={{marginTop:'20px'}}>
                <div className="sh">
                  <span className="sq">W clarity</span>
                  <span className="sn azure">{futureWScore}</span>
                </div>
                <div className="st">
                  <div className="sf2 azure" style={{width:`${futureWScore * 10}%`}}></div>
                  <input type="range" min="1" max="10" value={futureWScore} className="ar"
                    onChange={e => setFutureWScore(parseInt(e.target.value))} />
                </div>
                <div className="slbls"><span>Vague</span><span>Done is obvious</span></div>
              </div>
              <div style={{marginTop:'20px'}}>
                <button className="btn bp" onClick={handleSavePlan} disabled={futureSaving} style={{width:'100%',justifyContent:'center'}}>
                  {futureSaving ? 'Saving…' : `↑  Save Plan for ${formatNavDate(plannedDate)}`}
                </button>
              </div>
            </>
          )
        ) : (
          /* ── TODAY: full experience unchanged ── */
          <>
            <div className="flex gap16 ai-center mb20">
              <div className="var-badge azure">W</div>
              <div>
                <div style={{fontSize:'11px',color:'var(--text3)',fontFamily:'var(--ff-m)',letterSpacing:'0.18em',textTransform:'uppercase',marginBottom:'4px'}}>The Result Multiplier</div>
                <div style={{fontSize:'13px',color:'var(--text2)',lineHeight:'1.55'}}>A clear W turns concentration into measurable progress.</div>
              </div>
            </div>
            <label htmlFor="morning-w-goal" style={{display:'block',fontSize:'11px',color:'var(--text2)',fontWeight:'700',marginBottom:'10px'}}>
              Write your W in one sentence
            </label>
            <RichWinningResultEditor
              ref={goalRef}
              storageKey={`today-${todayISO}`}
              placeholder="Example: Send 10 personalized outreach messages to UK travel business owners before 12 PM."
              value={goal}
              onChange={handleGoalChange}
              onEnterCommit={handleCommitGoal}
            />
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:'10px',gap:'8px'}}>
              <div style={{fontSize:'11px',color:'var(--text3)'}}>
                A strong W is specific, measurable, and finishable today.
              </div>
              {yesterdaySeed && (
                <button
                  type="button"
                  className="seed-peek-btn"
                  onClick={() => setSeedPopupOpen(true)}
                  title="View yesterday's W seed"
                >
                  🌱 yesterday's seed
                </button>
              )}
            </div>
            {challengeVisible ? (
              <div ref={challengeRef} className={`w-challenge ${challengeClosing ? 'closing' : ''}`}>
                <div className="w-challenge-label">{activeChallenge.label}</div>
                <div className="w-challenge-question">{activeChallenge.question}</div>
                {activeChallenge.requiresInput ? (
                  <input ref={challengeInputRef} className="a-in w-challenge-input" type="text"
                    value={protectionNotList} onChange={(event) => setProtectionNotList(event.target.value)}
                    placeholder={activeChallenge.placeholder} />
                ) : null}
                <div className="w-challenge-actions">
                  <button type="button" className="btn bg-ghost" onClick={handleRewriteW}>Rewrite my W</button>
                  <button type="button" className="btn bp" onClick={handleConfirmRealW}>Yes — this is my real W</button>
                </div>
              </div>
            ) : null}
            <div className="sw">
              <div className="sh">
                <span className="sq">Can someone else understand what "done" means?</span>
                <span className="sn azure">{wScore}</span>
              </div>
              <div className="st">
                <div className="sf2 azure" style={{width:`${wScore * 10}%`}}></div>
                <input type="range" min="1" max="10" value={wScore} className="ar" onChange={(e) => handleWScoreChange(e.target.value)} />
              </div>
              <div className="slbls"><span>Vague</span><span>Done is obvious</span></div>
            </div>
          </>
        )}
      </div>

      {/* ── Checklist + actions: today only ── */}
      {isToday && (
        <>
          <div className="sec">Morning Ritual Checklist</div>
          <div className="card mb24">
            <div className="cg gold"></div>
            <div className="c-lbl">Complete before first deep work block</div>
            <div id="ritualList">
              {checklistItems.map((item, index) => (
                <div key={index} className="ci" onClick={() => toggleChecklistItem(index)}>
                  <div className={`cb ${checklist[index] ? 'on' : ''}`}></div>
                  <div className={`ct ${checklist[index] ? 'done' : ''}`}>{item}</div>
                </div>
              ))}
            </div>
            <div className="mt16">
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'10px',fontFamily:'var(--ff-m)',color:'var(--text3)',marginBottom:'6px'}}>
                <span>RITUAL PROGRESS</span>
                <span style={{color: completedCount === checklist.length ? 'var(--sage2)' : 'var(--text3)'}}>{completedCount}/{checklist.length}</span>
              </div>
              <div style={{height:'3px',background:'var(--bg3)',borderRadius:'2px',overflow:'hidden'}}>
                <div style={{height:'100%',width:`${(completedCount / checklist.length) * 100}%`,background: completedCount === checklist.length ? 'var(--sage2)' : 'var(--gold)',borderRadius:'2px',transition:'width 0.4s'}}></div>
              </div>
            </div>
          </div>
          <div className="insight">
            <div className="iq">
              Without W, your effort scatters. You can be busy for 8 hours and produce nothing of value. Write the goal first — everything else follows.
            </div>
          </div>
          <div className="flex gap12 mt24">
            <button className="btn bp" onClick={handleCommitGoal}>✓ &nbsp;Commit Goal & Start Day</button>
            <button className="btn bg-gold" onClick={() => navigate('/focus')}>Focus Blocks →</button>
          </div>
        </>
      )}
      {typeof document !== 'undefined' ? createPortal(traitModal, document.body) : null}

      {/* ── Yesterday's W seed popup ── */}
      {seedPopupOpen && typeof document !== 'undefined' && createPortal(
        <div className="seed-popup-overlay" onClick={() => setSeedPopupOpen(false)}>
          <div className="seed-popup" onClick={e => e.stopPropagation()}>
            <div className="seed-popup-header">
              <span className="seed-popup-icon">🌱</span>
              <span className="seed-popup-title">Yesterday&apos;s W Seed</span>
              <button className="seed-popup-close" onClick={() => setSeedPopupOpen(false)}>✕</button>
            </div>
            <div className="seed-popup-body">
              {yesterdaySeed}
            </div>
            <div className="seed-popup-hint">
              Written last night as tomorrow&apos;s W seed. Use it, adapt it, or start fresh.
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default MorningPanel
